/**
 * Senuto Sync Service — per-post SEO signals (traffic, positions, cannibalization).
 *
 * Stateless fetcher wzorzec jak `wpSyncService.ts`. Wywoluje REST API Senuto
 * przez nginx proxy `/api/senuto/*` (Bearer token server-side). W dev — Vite
 * proxy z `VITE_SENUTO_API_KEY`.
 *
 * Flow `syncSeoDataForWebsite`:
 *   1. get_domain_statistics → update websites.seo_domain_*
 *   2. get_positions_data (paginowane) → wszystkie keywords
 *   3. group by normalizeUrl(kw.url) → per-URL agregat
 *   4. get_cannibalization_keywords → flaguj pary kanibalizacyjne
 *   5. Update wp_posts seo_* + domeny bez rankingu → zera
 *
 * Source data granulation:
 *   - position.current/previous → diff (gained/lost)
 *   - searches.current → volume (do estimated traffic)
 *   - CTR model → est. traffic = sum(searches * CTR(position))
 *   - snippets.current → SERP features (AIO, PAA)
 */

import { supabase } from "@/integrations/supabase/client";
import { logPublic, logDebug, logError } from "@/services/contentEngine/contentLogger";
import { getApiTarget } from "@/lib/apiBase";

const PREFIX = "[SenutoSync]";
const DEFAULT_COUNTRY_ID = "200"; // Poland Base 2.0

// CTR model (Google desktop, SEO org)
const CTR_BY_POSITION: Record<number, number> = {
  1: 0.28, 2: 0.15, 3: 0.11, 4: 0.08, 5: 0.06,
  6: 0.05, 7: 0.04, 8: 0.03, 9: 0.025, 10: 0.02,
};
function ctrForPosition(pos: number): number {
  if (pos <= 0) return 0;
  if (pos <= 10) return CTR_BY_POSITION[pos] ?? 0.02;
  if (pos <= 20) return 0.01;
  if (pos <= 50) return 0.005;
  return 0;
}

// =============================================
// URL normalization
// =============================================

/**
 * Strip protocol, www, trailing slash, lowercase.
 * "https://WWW.Example.com/Foo/" → "example.com/foo"
 */
export function normalizeUrl(url: string): string {
  if (!url) return "";
  return url
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/$/, "")
    .trim();
}

// =============================================
// REST client (proxy)
// =============================================

interface ProxyRequestInit {
  method?: "GET" | "POST";
  body?: unknown;
  query?: Record<string, string | number | boolean>;
}

async function senutoFetch<T>(path: string, init: ProxyRequestInit = {}): Promise<T> {
  const method = init.method ?? "POST";
  const qs = init.query
    ? "?" + new URLSearchParams(
        Object.entries(init.query).map(([k, v]) => [k, String(v)]),
      ).toString()
    : "";
  // getApiTarget → browser: /api/senuto/* (nginx proxy dodaje Bearer)
  //              → Node worker: https://api.senuto.com/* + Bearer z process.env
  const target = getApiTarget("senuto", `${path}${qs}`);
  const res = await fetch(target.url, {
    method,
    headers: { "Content-Type": "application/json", ...target.headers },
    body: init.body ? JSON.stringify(init.body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Senuto ${method} ${path} → HTTP ${res.status}: ${text.slice(0, 200)}`);
  }
  return res.json() as Promise<T>;
}

// =============================================
// Senuto API response shapes (minimal)
// =============================================

interface SenutoDomainStatsResponse {
  data: {
    overview?: {
      top3?: number;
      top10?: number;
      top50?: number;
      visibility?: number;
      domain_rank?: number;
    };
  };
  success?: boolean;
}

interface SenutoKeywordStat {
  position: { current: number; previous: number; diff?: number };
  url: { current: string; previous?: string };
  searches: { current: number };
  cpc?: { current: number };
  difficulty?: { current: number };
  snippets?: { current: string[] };
}

interface SenutoKeywordRow {
  keyword: string;
  keyword_id: number;
  statistics: SenutoKeywordStat;
}

interface SenutoPositionsResponse {
  data: SenutoKeywordRow[];
  pagination?: { page_count: number; current_page: number; count: number };
  success?: boolean;
}

interface SenutoCannibalizationRow {
  keyword: string;
  statistics: {
    url: { current: string; previous: string };
    position: { current: number };
  };
}

interface SenutoCannibalizationResponse {
  data: SenutoCannibalizationRow[];
  success?: boolean;
}

// =============================================
// Public API
// =============================================

/**
 * GET /api/visibility_analysis/reports/dashboard/getDomainStatistics
 */
export async function fetchDomainStatistics(
  domain: string,
  countryId: string = DEFAULT_COUNTRY_ID,
): Promise<SenutoDomainStatsResponse["data"]> {
  const r = await senutoFetch<SenutoDomainStatsResponse>(
    "/api/visibility_analysis/reports/dashboard/getDomainStatistics",
    {
      method: "GET",
      query: { domain, country_id: countryId, fetch_mode: "topLevelDomain" },
    },
  );
  return r.data;
}

/**
 * POST /api/visibility_analysis/reports/positions/getData (paginowane)
 * Zwraca WSZYSTKIE keywords dla domeny (auto-pagination).
 */
export async function fetchAllPositions(
  domain: string,
  countryId: string = DEFAULT_COUNTRY_ID,
  onLog?: (msg: string) => void,
): Promise<SenutoKeywordRow[]> {
  const all: SenutoKeywordRow[] = [];
  let page = 1;
  const limit = 100;
  let totalPages = 1;

  do {
    const r = await senutoFetch<SenutoPositionsResponse>(
      "/api/visibility_analysis/reports/positions/getData",
      {
        method: "POST",
        body: {
          domain,
          country_id: countryId,
          fetch_mode: "topLevelDomain",
          limit,
          page,
        },
      },
    );
    all.push(...(r.data || []));
    totalPages = r.pagination?.page_count || 1;
    onLog?.(`page ${page}/${totalPages} — cumulative keywords: ${all.length}`);
    page++;
  } while (page <= totalPages && page < 20); // safety cap at 20 pages (~2000 kw)

  return all;
}

/**
 * POST /api/visibility_analysis/reports/cannibalization/getKeywords
 */
export async function fetchCannibalization(
  domain: string,
  countryId: string = DEFAULT_COUNTRY_ID,
): Promise<SenutoCannibalizationRow[]> {
  const r = await senutoFetch<SenutoCannibalizationResponse>(
    "/api/visibility_analysis/reports/cannibalization/getKeywords",
    {
      method: "POST",
      body: {
        domain,
        country_id: countryId,
        fetch_mode: "topLevelDomain",
        days_compare_mode: "week_ago_monday",
      },
    },
  );
  return r.data || [];
}

// =============================================
// Per-URL aggregation
// =============================================

interface PerUrlAggregate {
  url_normalized: string;
  total_keywords: number;
  top_3: number;
  top_10: number;
  top_50: number;
  best_keyword: string | null;
  best_position: number | null;
  best_keyword_volume: number;
  estimated_traffic: number;
  gained_count: number;
  lost_count: number;
  all_keywords: Array<{
    keyword: string;
    position: number;
    previous_position: number;
    volume: number;
    difficulty: number | null;
    snippets: string[];
  }>;
}

function aggregatePerUrl(rows: SenutoKeywordRow[]): Map<string, PerUrlAggregate> {
  const acc = new Map<string, PerUrlAggregate>();

  for (const r of rows) {
    const url = normalizeUrl(r.statistics.url?.current || "");
    if (!url) continue;

    if (!acc.has(url)) {
      acc.set(url, {
        url_normalized: url,
        total_keywords: 0,
        top_3: 0,
        top_10: 0,
        top_50: 0,
        best_keyword: null,
        best_position: null,
        best_keyword_volume: 0,
        estimated_traffic: 0,
        gained_count: 0,
        lost_count: 0,
        all_keywords: [],
      });
    }

    const agg = acc.get(url)!;
    const pos = r.statistics.position?.current ?? 0;
    const prev = r.statistics.position?.previous ?? 0;
    const vol = r.statistics.searches?.current ?? 0;

    agg.total_keywords++;
    if (pos > 0 && pos <= 3) agg.top_3++;
    if (pos > 0 && pos <= 10) agg.top_10++;
    if (pos > 0 && pos <= 50) agg.top_50++;

    // Best = najwyzsza (najmniejsza liczba) aktywna pozycja + wolumen jako tiebreaker
    if (
      pos > 0 &&
      (agg.best_position === null ||
        pos < agg.best_position ||
        (pos === agg.best_position && vol > agg.best_keyword_volume))
    ) {
      agg.best_keyword = r.keyword;
      agg.best_position = pos;
      agg.best_keyword_volume = vol;
    }

    // Gained/lost: pozycja SPADLA (higher number) → lost; pozycja WZROSLA (lower number) → gained
    if (prev > 0 && pos > 0) {
      if (pos < prev) agg.gained_count++;
      else if (pos > prev) agg.lost_count++;
    } else if (prev === 0 && pos > 0) {
      agg.gained_count++; // z niewidocznej na widoczna
    } else if (prev > 0 && pos === 0) {
      agg.lost_count++; // z widocznej na niewidoczna
    }

    agg.estimated_traffic += Math.round(vol * ctrForPosition(pos));

    if (agg.all_keywords.length < 50) {
      agg.all_keywords.push({
        keyword: r.keyword,
        position: pos,
        previous_position: prev,
        volume: vol,
        difficulty: r.statistics.difficulty?.current ?? null,
        snippets: r.statistics.snippets?.current ?? [],
      });
    }
  }

  return acc;
}

// =============================================
// Main orchestration
// =============================================

export interface SenutoSyncResult {
  total_posts: number;
  posts_with_rankings: number;
  total_keywords: number;
  total_cannibalization_pairs: number;
  duration_ms: number;
  domain_visibility: number | null;
  domain_top_10: number | null;
}

export interface SenutoSyncOptions {
  countryId?: string;
  onLog?: (msg: string) => void;
}

export async function syncSeoDataForWebsite(
  websiteId: string,
  options: SenutoSyncOptions = {},
): Promise<SenutoSyncResult> {
  const t0 = Date.now();
  const countryId = options.countryId ?? DEFAULT_COUNTRY_ID;
  const onLog = options.onLog;

  // 1. Load website + domain
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: website, error: wErr } = await (supabase as any)
    .from("websites")
    .select("id, url, name")
    .eq("id", websiteId)
    .maybeSingle();
  if (wErr || !website) throw new Error(`Website ${websiteId} not found`);

  const rawUrl = website.url || website.name || "";
  const cleanDomain = normalizeUrl(rawUrl).split("/")[0];
  if (!cleanDomain) throw new Error(`Nie znaleziono domeny dla website ${websiteId}`);

  logPublic(PREFIX, `Start sync dla ${cleanDomain}`);
  onLog?.(`▶ Sync SEO dla ${cleanDomain}`);

  // 2. Domain statistics
  const stats = await fetchDomainStatistics(cleanDomain, countryId);
  const overview = stats?.overview || {};
  const visibility = overview.visibility ?? null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await (supabase as any)
    .from("websites")
    .update({
      seo_domain_visibility: visibility,
      seo_domain_top_3_total: overview.top3 ?? 0,
      seo_domain_top_10_total: overview.top10 ?? 0,
      seo_domain_top_50_total: overview.top50 ?? 0,
      seo_domain_rank: overview.domain_rank ?? null,
      seo_domain_last_sync: new Date().toISOString(),
    })
    .eq("id", websiteId);

  onLog?.(`  domain stats: top3=${overview.top3} top10=${overview.top10} top50=${overview.top50} visibility=${visibility}`);

  // 3. All keywords (paginated)
  const keywords = await fetchAllPositions(cleanDomain, countryId, (msg) => onLog?.(`  positions ${msg}`));
  onLog?.(`  total keywords fetched: ${keywords.length}`);

  // 4. Group per URL
  const perUrl = aggregatePerUrl(keywords);
  onLog?.(`  unique ranking URLs: ${perUrl.size}`);

  // 5. Cannibalization
  const cannibRows = await fetchCannibalization(cleanDomain, countryId);
  onLog?.(`  cannibalization pairs: ${cannibRows.length}`);

  // Build cannibalization map: url_normalized → partner_url
  const cannibMap = new Map<string, string>();
  for (const row of cannibRows) {
    const a = normalizeUrl(row.statistics?.url?.current || "");
    const b = normalizeUrl(row.statistics?.url?.previous || "");
    if (a && b && a !== b) {
      cannibMap.set(a, b);
      cannibMap.set(b, a);
    }
  }

  // 6. Load wp_posts dla website
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: posts, error: pErr } = await (supabase as any)
    .from("wp_posts")
    .select("id, wp_url")
    .eq("website_id", websiteId);
  if (pErr) throw pErr;

  const allPosts = (posts || []) as Array<{ id: string; wp_url: string | null }>;
  let postsWithRankings = 0;

  const nowIso = new Date().toISOString();

  // 7. Per-post update
  for (const p of allPosts) {
    const postUrl = normalizeUrl(p.wp_url || "");
    const agg = postUrl ? perUrl.get(postUrl) : undefined;

    if (agg) {
      const partnerUrl = cannibMap.get(postUrl) || null;
      // Content decay heuristic
      const contentDecay =
        agg.lost_count >= 3 ||
        (agg.top_10 === 0 && agg.lost_count > agg.gained_count);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (supabase as any)
        .from("wp_posts")
        .update({
          seo_organic_traffic: agg.estimated_traffic,
          seo_total_keywords: agg.total_keywords,
          seo_top_3_count: agg.top_3,
          seo_top_10_count: agg.top_10,
          seo_top_50_count: agg.top_50,
          seo_best_keyword: agg.best_keyword,
          seo_best_position: agg.best_position,
          seo_best_keyword_volume: agg.best_keyword_volume,
          seo_gained_keywords_count: agg.gained_count,
          seo_lost_keywords_count: agg.lost_count,
          seo_content_decay: contentDecay,
          seo_has_cannibalization: !!partnerUrl,
          seo_cannibalization_partner_url: partnerUrl,
          seo_all_keywords: agg.all_keywords,
          seo_data_source: "senuto",
          seo_last_sync: nowIso,
        })
        .eq("id", p.id);
      postsWithRankings++;
    } else {
      // Post bez rankingu — zeruj + oznacz sync
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (supabase as any)
        .from("wp_posts")
        .update({
          seo_organic_traffic: 0,
          seo_total_keywords: 0,
          seo_top_3_count: 0,
          seo_top_10_count: 0,
          seo_top_50_count: 0,
          seo_best_keyword: null,
          seo_best_position: null,
          seo_best_keyword_volume: 0,
          seo_gained_keywords_count: 0,
          seo_lost_keywords_count: 0,
          seo_content_decay: false,
          seo_has_cannibalization: false,
          seo_cannibalization_partner_url: null,
          seo_all_keywords: [],
          seo_data_source: "senuto",
          seo_last_sync: nowIso,
        })
        .eq("id", p.id);
    }
  }

  const duration_ms = Date.now() - t0;
  logPublic(
    PREFIX,
    `Done ${duration_ms}ms: ${postsWithRankings}/${allPosts.length} posts with rankings, ${keywords.length} keywords, ${cannibRows.length} cannib pairs`,
  );
  onLog?.(
    `✓ Done ${(duration_ms / 1000).toFixed(1)}s: ${postsWithRankings}/${allPosts.length} wpisów z rankingami, ${keywords.length} keys, ${cannibRows.length} kanibalizacji`,
  );

  // Auto-recompute Decision Engine (non-blocking) — M15
  try {
    const { recomputeFinalActionsForWebsite } = await import("@/services/decisionEngine");
    onLog?.("▶ Auto-recompute Decision Engine po sync SEO...");
    await recomputeFinalActionsForWebsite(websiteId, {
      onLog: (_type, msg) => onLog?.(msg),
    });
  } catch (err) {
    logError(PREFIX, `Auto-recompute Decision Engine failed: ${err instanceof Error ? err.message : String(err)}`);
  }

  // M16 auto-run: L5 coverage refresh dla wszystkich map tego website
  try {
    const { auditMapCoverageOnly } = await import("@/services/validation/mapAuditOrchestrator");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: maps } = await (supabase as any)
      .from("thematic_maps").select("id").eq("website_id", websiteId);
    const mapIds = ((maps || []) as Array<{ id: string }>).map((m) => m.id);
    for (const mapId of mapIds) {
      await auditMapCoverageOnly(mapId, { onLog: (_t, m) => onLog?.(m) });
    }
    onLog?.(`✓ L5 coverage recompute: ${mapIds.length} map`);
  } catch (err) {
    logError(PREFIX, `L5 coverage recompute failed: ${err instanceof Error ? err.message : String(err)}`);
  }

  return {
    total_posts: allPosts.length,
    posts_with_rankings: postsWithRankings,
    total_keywords: keywords.length,
    total_cannibalization_pairs: cannibRows.length,
    duration_ms,
    domain_visibility: visibility,
    domain_top_10: overview.top10 ?? null,
  };
}

// Legacy re-export for potential direct caller
export { logDebug as _debug, logError as _err };
